// fileUtils.ts
import imageCompression from 'browser-image-compression';
import { IFile } from 'entities/task-new/types/Task-interface';

// Допустимые MIME-типы
export const acceptedMimeTypes = [
    'image/jpeg', 'image/pjpeg', 'image/jpg',
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel.sheet.macroenabled.12',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/x-zip-compressed',
    'application/x-rar-compressed'
];

// Функция преобразования расширения в MIME-тип
export const convertExtToMime = (ext: string): string => {
    switch (ext.toLowerCase()) {
        case 'jpg':
        case 'jpeg': return 'image/jpeg';
        case 'pdf': return 'application/pdf';
        case 'xls': return 'application/vnd.ms-excel';
        case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case 'xlsm': return 'application/vnd.ms-excel.sheet.macroenabled.12';
        case 'doc': return 'application/msword';
        case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        case 'zip': return 'application/x-zip-compressed';
        case 'rar': return 'application/x-rar-compressed';
        default: return ext;
    }
};

// Функция преобразования файла в Base64
export const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
        };
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
    });
};

// Обработчик загрузки файлов с проверками
export const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    formData: any,
    setFormData: (value: any) => void,
    setFileError: (value: string | null) => void,
    setIsCompressing: (value: boolean) => void
): Promise<void> => {
    const input = event.target;
    const newFiles = input.files;
    if (!newFiles || newFiles.length === 0) return;

    setFileError(null);
    setIsCompressing(true);

    const MAX_FILE_SIZE_MB = 20; // Максимальный размер файла
    const MAX_TOTAL_FILES = 20; // Максимальное количество файлов
    const selectedFiles = Array.from(newFiles);

    // Проверка количества файлов
    if (formData.Files.length + selectedFiles.length > MAX_TOTAL_FILES) {
        setFileError(`Максимальное количество файлов: ${MAX_TOTAL_FILES}`);
        setIsCompressing(false);
        return;
    }

    // Проверка типов файлов
    const invalidFiles = selectedFiles.filter(file => {
        const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
        const mimeType = convertExtToMime(fileExt);
        return !acceptedMimeTypes.includes(mimeType);
    });

    if (invalidFiles.length > 0) {
        setFileError(`Недопустимый формат: ${invalidFiles[0].name}`);
        setIsCompressing(false);
        return;
    }

    // Проверка размера файлов
    const oversizedFiles = selectedFiles.filter(file =>
        file.size > MAX_FILE_SIZE_MB * 1024 * 1024
    );

    if (oversizedFiles.length > 0) {
        setFileError(
            `Файл слишком большой: ${oversizedFiles[0].name} (макс. ${MAX_FILE_SIZE_MB} МБ)`
        );
        setIsCompressing(false);
        return;
    }

    try {
        // Разделяем файлы на изображения и остальные
        const imageFiles = selectedFiles.filter(file =>
            ['image/jpeg', 'image/pjpeg', 'image/jpg'].includes(file.type)
        );
        const otherFiles = selectedFiles.filter(file =>
            !['image/jpeg', 'image/pjpeg', 'image/jpg'].includes(file.type)
        );

        // Сжимаем изображения
        const compressedFiles = await Promise.all(
            imageFiles.map(async file => {
                try {
                    const options = {
                        maxSizeMB: 1,
                        maxWidthOrHeight: 1024,
                        useWebWorker: true,
                    };
                    return await imageCompression(file, options);
                } catch (error) {
                    console.error('Ошибка сжатия:', error);
                    return file; // Возвращаем оригинал при ошибке
                }
            })
        );

        const allFiles = [...compressedFiles, ...otherFiles];

        const newFileItems: IFile[] = [];
        for (const file of allFiles) {
            const base64Data = await convertFileToBase64(file);
            newFileItems.push({
                Name: file.name,
                DataBase64: base64Data
            });
        }

        setFormData((prev: any) => ({
            ...prev,
            Files: [...(prev.Files || []), ...newFileItems]
        }));

    } catch (error) {
        console.error('Ошибка обработки файлов:', error);
        setFileError('Ошибка при обработке файлов');
    } finally {
        setIsCompressing(false);
        input.value = '';
    }
};